import type { FetchReviewsParams } from 'api/review'
import { deletePhoto, deleteReview, fetchReviews, getReviewSummary, uploadPhoto, upsertReview } from 'api/review'
import { API_CALL, USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE, USER_REVIEW_SET_REVIEW_STAGE } from './actionConstants'

import {
  DELETE_REVIEW,
  DELETE_REVIEW_PHOTO,
  FETCH_REVIEW_SUMMARY,
  FETCH_REVIEWS,
  FETCH_REVIEWS_ORDER,
  FETCH_USER_NUMBER_OF_REVIEWS,
  UPLOAD_REVIEW_PHOTO,
  UPSERT_REVIEW,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'
import getObjectKey from 'lib/object/getObjectKey'
import { fillArray } from 'lib/array/arrayUtils'

export function setReviewPhotoUploadPercentage(
  uploadId: string,
  percentage: number,
): AppAction {
  return {
    type: USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE,
    uploadId,
    percentage,
  }
}

export function setReviewFormPage(
  reviewStage: 'initial' | 'additional_fields' | 'submitted' | null,
): AppAction {
  return {
    type: USER_REVIEW_SET_REVIEW_STAGE,
    reviewStage,
  }
}

export function fetchUserReviews(params: FetchReviewsParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const { limit = 4, offset = 0, ...keyParams } = params

    const key = getObjectKey(keyParams)
    const { reviews, total } = state.review.reviews[key] ?? { reviews: [], total: 999 }
    // can't ask for more records than the total
    const existingPositions = new Set(reviews.map(record => record.position))
    const expectedPositions = fillArray(limit).map((_, index) => Math.min((index + 1) + offset, total))
    if (expectedPositions.every(position => existingPositions.has(position))) {
      // already have the records we want
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_REVIEWS,
      request: async() => {
        const { reviews, total } = await fetchReviews(params)
        return {
          reviews: reviews.map((review, index) => ({
            review,
            position: (index + 1) + offset,
          })),
          total,
        }
      },
      key,
    })
  }
}

export function fetchUserReviewsByOrder(orderId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.review.reviewsByOrder[orderId] ||
      state.review.reviewsByOrderLoading[orderId]
    ) {
      // already fetching or have the records
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_REVIEWS_ORDER,
      request: () => fetchReviews({ orderId }),
      orderId,
    })
  }
}

export function upsertUserReview(
  params: App.CreateReviewFormParams | App.UpdateReviewParams,
  reviewStage?: 'initial' | 'additional_fields' | 'submitted' | null,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!state.auth.account.memberId) return

    const isCreate = !params.id
    const userName = params.userName || state.auth.account.fullName

    const data = isCreate ?
        {
          ...params,
          userName,
          userId: state.auth.account.memberId,
          ...(userName !== 'Anonymous' && {
            userEmail: state.auth.account.email,
            userCountry: state.auth.account.countryCode,
          }),
        } as App.CreateReviewParams :
        {
          ...params,
          userName,
          ...(userName !== 'Anonymous' && {
            userEmail: state.auth.account.email,
            userCountry: state.auth.account.countryCode,
          }),
        } as App.UpdateReviewParams

    dispatch({
      reviewStage,
      isCreate,
      type: API_CALL,
      api: UPSERT_REVIEW,
      request: () => upsertReview(data),
    })
  }
}

export function removeReview(review: App.Review, orderId: string): AppAction {
  return {
    orderId,
    type: API_CALL,
    api: DELETE_REVIEW,
    request: () => deleteReview(review),
  }
}

/**
 * Uploads the photo to svc-review
 */
export function uploadReviewPhoto(uploadId: string, file: File, orderId: string): AppAction {
  return (dispatch) => {
    dispatch({
      uploadId,
      type: API_CALL,
      api: UPLOAD_REVIEW_PHOTO,
      request: () => uploadPhoto(
        file,
        orderId,
        (percentage) => dispatch(setReviewPhotoUploadPercentage(uploadId, percentage)),
      ),
    })
  }
}

/**
 * Deletes the photo from svc-review
 */
export function deleteReviewPhoto(imageId: string, orderId: string): AppAction {
  return {
    type: API_CALL,
    api: DELETE_REVIEW_PHOTO,
    request: () => deletePhoto(imageId, orderId),
  }
}

export function fetchCustomerReviewSummary(propertyId: string, propertyProvider: 'bedbank' | 'luxuryescapes'): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = getObjectKey({ propertyId, propertyProvider })

    if (state.review.reviewSummaries[key]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_REVIEW_SUMMARY,
      request: () => getReviewSummary(propertyId, propertyProvider),
      key,
    })
  }
}

export function fetchUserNumberOfReviews(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const userId = state.auth.account.memberId

    if (!userId) return

    dispatch({
      type: API_CALL,
      api: FETCH_USER_NUMBER_OF_REVIEWS,
      request: () => fetchReviews({ userId }),
    })
  }
}
